import { Box, CircularProgress, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { CContainedButton, CLottie } from 'components/atoms';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useStoredCardsQuery } from 'services/payment';
import { ReactComponent as CardIcon } from 'assets/svg/card.svg';
import { ReactComponent as AddIcon } from 'assets/svg/add-primary.svg';
import { useTranslation } from 'react-i18next';
import { useAppContext } from 'hooks/context';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import AddCreditCard from './components/add-credit-card';
import { useChangeMonthlySubscriptionCreditCardMutation } from 'services/patient-subscriptions';
import { useAppSelector } from 'hooks/redux';
import { enqueueSnackbar } from 'notistack';

export default function UpdatePaymentMethod({ membership }: any) {
  const user = useAppSelector((state) => state.auth.user);
  const { t } = useTranslation(['common', 'translation']);
  const [viewType, setViewType] = useState<null | number>(null); // 1 for add new card, 2 for stored cards
  const { dialog } = useAppContext();

  const { data, isLoading, refetch } = useStoredCardsQuery(
    {},
    {
      refetchOnMountOrArgChange: true,
    }
  );
  const [changeMonthlySubscriptionCreditCard, { isLoading: isChangingCreditCard }] =
    useChangeMonthlySubscriptionCreditCardMutation();

  const schema = z.object({
    selectedCardId: z.number().min(1, t('translation:schema.required')),
  });

  const {
    control,
    setValue,
    watch,
    trigger,
    getValues,
    reset,
    handleSubmit,
    formState: { touchedFields, errors, isValid },
  } = useForm({
    defaultValues: {
      selectedCardId: undefined,
    },
    resolver: zodResolver(schema),
    mode: 'all',
  });

  const handleSelectedCardChange = (id: number) => {
    const selectedCard = data?.data?.cards.find((card: CardModel) => card.id === id);
    if (selectedCard) {
      setValue('selectedCardId', selectedCard.id);
      trigger('selectedCardId');
    }
  };

  const onSubmit = (formData: any) => {
    changeMonthlySubscriptionCreditCard({
      profile: {
        ...user!,
        profileId: membership.patientId,
      },
      data: {
        patientSubscriptionId: membership.patientSubscriptionId,
        creditCardId: formData.selectedCardId,
      },
    })
      .unwrap()
      .then(({ isSuccess }) => {
        if (isSuccess) {
          enqueueSnackbar(t('common:successful-update-card'), { variant: 'success' });
        }
      });
  };

  const checkCardLimit = () => {
    const isHasFiveCards = data?.data?.cards?.length >= 5;
    if (isHasFiveCards) {
      dialog.showDialog({
        type: 'warning',
        message: t('translation:form.credit-card-limit-message'),
        onClose: () => {
          dialog.closeDialog();
        },
      });
      return true;
    }
    return false;
  };

  const handleViewTypeChange = (type: number) => {
    if (type === 2 && checkCardLimit()) return;

    setViewType(type);

    if (type === 1) {
      setViewType(2);
    } else {
      setViewType(1);
    }
  };

  const getInitialSelectedCardId = () => {
    if (!membership?.paymentCard?.id || !data?.data?.cards) {
      return undefined;
    }

    const cardExistsInList = data.data.cards.some(
      (card: CardModel) => card.id === membership.paymentCard.id
    );

    return cardExistsInList ? membership.paymentCard.id : undefined;
  };

  useEffect(() => {
    if (data?.data?.cards) {
      const initialCardId = getInitialSelectedCardId();
      console.log(initialCardId);
      setValue('selectedCardId', initialCardId);
      trigger('selectedCardId');
    }
  }, [data, membership, reset]);

  const handleCardAdded = () => {
    refetch();
    setViewType(2);
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <CLottie lottieStyle="w-12 h-12" />
      </div>
    );
  }
  const hasCards = data?.data?.cards?.length > 0;
  const currentView = viewType ?? (hasCards ? 2 : 1);

  // yeni kart ekleme formu
  if (currentView === 1) {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <div className="text-body16 font-normal text-neutral-600">
            {t('translation:form.add-card')}
          </div>
          <button className="text-label14 font-normal text-primary-600" onClick={() => setViewType(2)}>
            {t('translation:form.my-stored-cards')}
          </button>
        </div>
        <hr />
        <AddCreditCard onSuccess={handleCardAdded} />
      </div>
    );
  }

  // kayıtlı kartlardan seçim
  if (currentView === 2) {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex justify-between">
          <div className="text-body16 font-normal text-neutral-600">
            {t('translation:form.my-stored-cards')}
          </div>

          <button className="flex items-center gap-[10px]" onClick={() => handleViewTypeChange(2)}>
            <div className="h-6 w-6 rounded-[4px] bg-[#F3F4F9]">
              <AddIcon className="h-full w-full" />
            </div>
            <span className="text-body14 font-normal text-primary-500">
              {t('translation:form.add-card')}
            </span>
          </button>
        </div>
        <hr />

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-4 flex flex-col gap-6">
            <Controller
              control={control}
              name="selectedCardId"
              rules={{ required: t('translation:schema.required') }}
              render={({ field }) => (
                <RadioGroup
                  {...field}
                  aria-labelledby="demo-radio-buttons-group-label"
                  name="radio-buttons-group"
                  value={field.value?.toString() || ''}
                  onChange={(e) => {
                    console.log(e.target.value);
                    field.onChange(e.target.value);
                    handleSelectedCardChange(Number(e.target.value));
                  }}
                >
                  {data?.data?.cards.map((cards: CardModel, index) => (
                    <FormControlLabel
                      key={index + 1}
                      control={
                        <Radio
                          sx={{
                            color: '#C19ADE',
                            '&.Mui-checked': {
                              color: '#C19ADE',
                            },
                          }}
                        />
                      }
                      componentsProps={{ typography: { width: '100%' } }}
                      value={cards.id}
                      label={
                        <Box
                          component="div"
                          sx={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'space-between',
                            color: '#727A83',
                            fontWeight: 400,
                            fontSize: '14px',
                          }}
                        >
                          <div className="flex items-center">
                            <CardIcon />
                            <div className="ml-3 w-36 text-neutral-600">
                              {cards.alias !== '' ? cards.alias : cards.holderName}
                            </div>
                            <div className="">
                              {cards.binNumber} ****** {cards.lastFourDigits}
                            </div>
                          </div>
                          <div>
                            {cards.expireMonth}/{cards.expireYear}
                          </div>
                        </Box>
                      }
                    />
                  ))}
                </RadioGroup>
              )}
            />
          </div>

          <CContainedButton
            type="submit"
            color="primary"
            fullWidth
            disabled={isChangingCreditCard || !isValid}
          >
            {isChangingCreditCard ? <CircularProgress size={24} /> : t('common:update')}
          </CContainedButton>
        </form>
      </div>
    );
  }

  return null;
}

